import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';
import { handleCloseModal, paramsToHash } from '../calendar.model';
import {
  ADD_PASSENGER,
  DEFAULT_WEIGHT,
  EDIT_PASSENGER,
  LOAD_MANIFEST,
  WEIGHT_AND_BALANCE,
  WEIGHT_UNITS
} from './weight-and-balance.consts';
import EventContainer from '../modals/calendar.event-container';
import {
  calculateWeightAndBalance,
  getWeightAndBalance,
  saveWeightAndBalance,
  seedWbEventContext,
} from './weight-and-balance.actions';
import PassengerSeats from './passengers-seats/passegers-seats.js';
import WeightAndBalanceTable from './weight-and-balance.table.js';
import WeightAndBalanceFuel from './weight-and-balance.fuel.js';

import InvalidData from '../../ui/error-states/invalid-data';
import {
  convertToAbsoluteWeight,
  getWeightAndBalanceFields,
  normalizeValue
} from './weight-and-balance.helpers';
import Indication from '../../ui/indication';
import { selectUIMessages } from '../calendar.selectors';
import { PrinterService } from '../../printer/printer.service';
import { getWeightAndBalanceManifest } from './weight--and-balance-manifest/weight-and-balance-manifest.helpers';
import ZoomImg from './components/zoom-img';

export const fuelWeight = 'Fuel Weight';
export const taxiRunUpFuelBurn = 'Taxi & Run-up Fuel Burn';
export const eventDuration = 'Duration of Flight';
export const fuelLoad = 'Fuel Load';
export const fuelLoadType = 'Fuel Load Type';

const fieldsNames = {
  [fuelWeight]: 'fuelWeight',
  [taxiRunUpFuelBurn]: 'taxiRunUpFuelBurn',
  [eventDuration]: 'eventDuration',
  [fuelLoad]: 'fuelLoad',
  [fuelLoadType]: 'fuelLoadType',
};

class WeightAndBalance extends React.Component {
  static footer = true;

  constructor(props) {
    super(props);

    this.form = null;

    this.handleAddPassenger = this.handleSwitchModals.bind(this, ADD_PASSENGER);
    this.handleEditPassenger = this.handleSwitchModals.bind(this, EDIT_PASSENGER);

    this.handleRemovePassengers = this.handleRemovePassengers.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleMovePassengers = this.handleMovePassengers.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.setFormRef = this.setFormRef.bind(this);
    this.handleCalculate = this.calculate.bind(this);
    this.handleClose = handleCloseModal.bind(this, this.props);
    this.handleLoadManifest = this.handleLoadManifest.bind(this);
    this.handleChangFuelLoadType = this.handleChangFuelLoadType.bind(this);

    const {
      weightAndBalance: {
        unit = WEIGHT_UNITS.percent,
        fuelType = WEIGHT_UNITS.lbs,
        weightAndBalanceTable
      }
    } = this.props;
    const fields = getWeightAndBalanceFields(weightAndBalanceTable);
    const status = this.getStatusCode(fields);

    this.state = {
      fields,
      unit,
      fuelType,
      status,
    };
  }

  componentDidMount() {
    const { getWeightAndBalance } = this.props;
    getWeightAndBalance();
    this.getAircraftPilotPosition();
  }

  getStatusFromFields = (weightAndBalance) => {
    const warnings = weightAndBalance.warning || [];

    Object.keys(weightAndBalance.weightAndBalanceTable).forEach((fieldName) => {
      const field = weightAndBalance.weightAndBalanceTable[fieldName];

      if (field.status && field.status.length) {
        warnings.push(field.status[0]);
      }
    })

    return warnings.filter((item, pos) => {
      return warnings.indexOf(item) === pos;
    })
  }

  componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>, snapshot: SS) {
    const {weightAndBalance, resources} = this.props;
    const {unit} = this.state;

    const weightAndBalanceTable = weightAndBalance.weightAndBalanceTable;

    const prevWarnings = this.getStatusFromFields(prevProps.weightAndBalance);
    const curWarnings = this.getStatusFromFields(weightAndBalance);

    if (prevWarnings.length !== curWarnings.length || prevProps.weightAndBalance !== weightAndBalance) {
      const fields = getWeightAndBalanceFields(weightAndBalanceTable);
      const newUnit = weightAndBalance.unit || WEIGHT_UNITS.percent;
      const fuelType = weightAndBalance.fuelType || WEIGHT_UNITS.percent;

      const status = this.getStatusCode(fields);

      this.setState({
        fields,
        status,
        unit: newUnit,
        prevParams: this.getParams(fields, unit, fuelType),
      });
    }

    if (
      Object.keys(prevProps.resources).length !== Object.keys(resources).length
      || prevProps.weightAndBalance.eventId !== weightAndBalance.eventId) {
      this.getAircraftPilotPosition();
    }
  }

  getAircraftPilotPosition() {
    const { weightAndBalance, resources, wbEventContext } = this.props;
    // Month-view fast path: pilotRight seeded alongside aircraftId
    if (wbEventContext && wbEventContext.eventId === String(weightAndBalance.eventId)) {
      this.setState({ isPilotRight: wbEventContext.pilotRight === true });
      return;
    }
    // Legacy day-view path
    const currentResource = Object.keys(resources).find(resId => {
      const resource = resources[resId];
      if (resource && resource.events && Array.isArray(resource.events)) {
        return resource.events.includes(weightAndBalance.eventId) && resource.resourceTypeName === "Aircraft";
      }
    });
    if (currentResource) {
      this.setState({
        isPilotRight: resources[currentResource].pilotRight === "1"
      });
    }
  }


  getStatusCode(fields) {
    const {weightAndBalance = {}} = this.props;
    const {warning = []} = weightAndBalance;
    const localWarnings = warning.slice();

    let statuses = [];
    const formStatus = fields.find((item) => item.status && !item.title);
    const arrayIndex = fields.indexOf(formStatus);

    fields.splice(arrayIndex, 1);
    if (formStatus && formStatus.status) {
      statuses.push(formStatus.status[0]);
    }

    fields.forEach((field) => {
      if (field.status && field.status.length && field.title) {
        const warningArrayIndex = localWarnings.indexOf(field.status[0]);
        if (warningArrayIndex !== -1) {
          localWarnings.splice(warningArrayIndex, 1);
        }
        statuses.push({
          success: false,
          message: field.status[0]
        })
      }
    })

    localWarnings.forEach((errorMessage) => {
      statuses.push({
        success: false,
        message: errorMessage
      })
    })

    statuses.filter((item, pos) => {
      return statuses.indexOf(item) === pos;
    });

    const messages = statuses.map(status => status.message);
    const filteredStatuses = statuses
      .filter(({message}, index) => messages.indexOf(message) === index);

    return filteredStatuses.length ? filteredStatuses : []
  }

  handleSwitchModals(type, e) {
    const { seat } = e.currentTarget.dataset;
    const { URL, switchModals } = this.props;

    const position = seat ? JSON.parse(seat) : null;

    if (position) {
      const nextBreadcrumbs = URL.eventBreadcrumbs.concat({
        ...URL.eventParams,
        action: type
      });

      switchModals(
        type,
        {
          ...URL.eventParams,
          ...position
        },
        nextBreadcrumbs
      );
    }
  }

  getParams(fields, fuelType) {
    const rows = fields
      .filter(item => item.group === 'row')
      .map(item => ({ name: item.weight.name, weight: normalizeValue(item.weight.value, 2) }));

    const values = fields.reduce((acc, item) => {
      if (item.weight.editable && !item.group) {
        let value = normalizeValue(item.weight.value, 2);
        if (fieldsNames[item.id] === fieldsNames[eventDuration]) {
          value = normalizeValue(item.weight.value, 10);
        }
        return {
          ...acc,
          [fieldsNames[item.id]]: value,
        };
      }

      return acc;
    }, {});

    return {
      unit: fuelType,
      rows,
      ...values
    };
  }

  async calculate(force = false) {
    const {calculateWeightAndBalance} = this.props;
    const {fields, unit, prevParams} = this.state;

    if (this.form && this.form.reportValidity()) {
      const params = this.getParams(fields, unit);

      if (force || !isEqual(params, prevParams)) {
        await calculateWeightAndBalance(params);

        const { activeElement } = document;

        if (activeElement.tagName.toUpperCase() === 'INPUT') {
          const index = parseInt(activeElement.dataset.index, 10);
          if (index > -1 && !parseFloat(activeElement.value, 10)) {
            this.handleChange(index, '');
          }
        }
        this.setState({ prevParams: params });
      } else {
        this.fillInEmptyFields();
      }
    }
  }

  fillInEmptyFields() {
    const { fields } = this.state;

    this.setState({
      fields: fields.map(field => ({
        ...field,
        weight: { ...field.weight, value: field.weight.value || DEFAULT_WEIGHT }
      }))
    });
  }

  handleChange(index, value) {
    const { fields, unit } = this.state;

    const updatedFields = fields.map((field, i) => {
      if (index !== i) {
        return field;
      }
      const isSelect = field.weight.select;
      const newValue = isSelect ? value : convertToAbsoluteWeight(value, unit);

      return { ...field, weight: { ...field.weight, value: newValue} }
    })

    this.setState({
      fields: updatedFields,
    });
  }

  handleChangFuelLoadType(unit) {
    this.setState({unit: unit});
  }

  async handleMovePassengers(a, b) {
    const {
      saveWeightAndBalance,
      weightAndBalance: { passengers }
    } = this.props;

    const updatedPassengers = passengers.map((row, r) =>
      row.map((col, c) => {
        if (a.row === r && a.col === c) return passengers[b.row][b.col];
        if (b.row === r && b.col === c) return passengers[a.row][a.col];

        return col;
      })
    );

    await saveWeightAndBalance({
      passengers: updatedPassengers
    });

    await this.calculate(true);
  }

  async handleRemovePassengers(row, col) {
    const {
      saveWeightAndBalance,
      weightAndBalance: { passengers }
    } = this.props;

    if (confirm('Remove passenger?')) {
      const updatedPassengers = passengers.map((item, _row) => {
        return item.map((element, _col) => {
          if (col === _col && row === _row) {
            return null;
          }

          return element;
        });
      });

      await saveWeightAndBalance({
        passengers: updatedPassengers
      });

      await this.calculate(true);
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    const { saveWeightAndBalance } = this.props;
    const { fields, unit } = this.state;

    const params = this.getParams(fields, unit);

    await saveWeightAndBalance(params);
    this.handleClose();
  }

  setFormRef(ref) {
    this.form = ref;
  }

  async handleLoadManifest() {
    const { saveWeightAndBalance, dispatch } = this.props;
    const { fields, unit, fuelType} = this.state;

    await saveWeightAndBalance(this.getParams(fields, unit, fuelType));
    PrinterService.print(getWeightAndBalanceManifest(dispatch));
  }

  onCopy = (name: string) => {
    const { weightAndBalance: {fuelWeightTable}} = this.props;
    const {unit, fields} = this.state;
    const fuelDataItem = fuelWeightTable[name];
    const fuelLoadIndex = fields.findIndex((item)=> item.id === "Fuel Load");
    let fuelLoadValue;

    if (unit === "%") {
      fuelLoadValue = fuelDataItem.percent;
    } else {
      fuelLoadValue = fuelDataItem[unit];
    }
    this.handleChange(fuelLoadIndex, fuelLoadValue)
  }

  render() {
    const {
      weightAndBalance: {passengers, image, rows, fuelWeightTable},
      UI,
      UIMessages
    } = this.props;

    const {fields, unit, fuelType, status, isPilotRight} = this.state;

    // TODO remove after  backend fix
    let safePassengers = passengers;
    if (typeof safePassengers === "string") {
      safePassengers = JSON.parse(`${safePassengers}`);
    }

    const canEdit = rows.length;

    return (
      <EventContainer>
        <form onSubmit={this.handleSubmit} ref={this.setFormRef}>
          <div className='form-group modal-passengers-container'>
            {canEdit ? (
              <React.Fragment>
                <PassengerSeats
                  isPilotRight={isPilotRight}
                  seats={rows}
                  passengers={safePassengers}
                  onAdd={this.handleAddPassenger}
                  onEdit={this.handleEditPassenger}
                  onRemove={this.handleRemovePassengers}
                  onMove={this.handleMovePassengers}
                />
                {!!(fields.length) && (
                  <WeightAndBalanceTable
                    onChange={this.handleChange}
                    onCalculate={this.handleCalculate}
                    onChangeFuelLoadType={this.handleChangFuelLoadType}
                    unit={unit}
                    fuelType={fuelType}
                    status={status}
                    fields={fields}
                    fuelLoad={fuelLoad}
                  />
                )}
                <div className='weight-image-and-fuel'>
                  {fuelWeightTable && (
                    <WeightAndBalanceFuel data={fuelWeightTable} onCopy={this.onCopy}/>
                  )}
                  {image ? (
                    <ZoomImg
                      image={image} title='Weight and Balance schema'
                      imgClass='weight-image'
                      showOnMobile={false}
                    />
                  ) : null}
                </div>

              </React.Fragment>
            ) : UI.progress ? null : (
              <InvalidData hint='To have ability to add passengers go to aircraft settings and add seats rows' />
            )}
          </div>
          <input
            id={WEIGHT_AND_BALANCE}
            type='submit'
            style={{display: 'none'}}
            disabled={!canEdit}
          />
          <input
            id={`${WEIGHT_AND_BALANCE}-cancel`}
            onClick={this.handleClose}
            type='button'
            style={{ display: 'none' }}
          />
          <input
            id={LOAD_MANIFEST}
            onClick={this.handleLoadManifest}
            type='button'
            style={{display: 'none'}}
          />
        </form>
        <Indication className='event-form-indication' messages={UIMessages} />
      </EventContainer>
    );
  }
}

const mapStateToProps = state => ({
  UIMessages: selectUIMessages(WEIGHT_AND_BALANCE)(state),
  UI: state.UI,
  URL: state.URL,
  scrollY: state.userUI.scrollY,
  resources: state.resources,
  weightAndBalance: state.weightAndBalance,
  wbEventContext: state.wbEventContext,
});

const mapDispatchToProps = dispatch => ({
  dispatch,
  ...bindActionCreators(
    {
      calculateWeightAndBalance,
      getWeightAndBalance,
      saveWeightAndBalance,
      seedWbEventContext,
      paramsToHash
    },
    dispatch
  )
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(WeightAndBalance);
